import React from 'react';
import { createPortal } from 'react-dom';

import { DhlModal } from '@/vendor/dhlReactInitial';

import type { ComponentProps } from 'react';

type DhlModalProps = ComponentProps<typeof DhlModal>;
type ModalType = 'alert' | 'fullscreen';

interface DhlModalWrapperLoginProps extends Omit<
    DhlModalProps,
    'isFullScreenOnMobile' | 'className' | 'dataClassName'
> {
    modalType?: ModalType;
    className?: string;
    dataClassName?: string;
    desktopMaxHeightClassName?: string;
    portalToBody?: boolean;
    children?: React.ReactNode;
}

/**
 * Login-only modal facade. Keeping the app-wide modal wrapper on the legacy
 * facade prevents protected routes from changing their module graph.
 */
const DhlModalWrapperLogin: React.FC<DhlModalWrapperLoginProps> = ({
    modalType = 'fullscreen',
    className,
    dataClassName,
    desktopMaxHeightClassName,
    portalToBody = false,
    children,
    ...rest
}) => {
    const isAlert = modalType === 'alert';
    const resolvedClassName = isAlert
        ? `tw-flex tw-items-center tw-justify-center${className ? ` ${className}` : ''}`
        : className;
    const defaultDesktopWidth = isAlert
        ? 'tablet:!tw-w-[min(38.06rem,calc(100vw-10rem))] desktop:!tw-w-[min(38.06rem,calc(100vw-2rem))]'
        : `tablet:!tw-w-[min(55.25rem,calc(100vw-10rem))] desktop:!tw-w-[min(55.25rem,calc(100vw-2rem))] ${
              desktopMaxHeightClassName ?? 'desktop:!tw-max-h-[calc(100dvh-12.5rem)]'
          }`;
    const resolvedDataClassName = dataClassName
        ? `${dataClassName} ${defaultDesktopWidth}`
        : defaultDesktopWidth;

    const modal = (
        <DhlModal
            {...rest}
            isFullScreenOnMobile={!isAlert}
            fixedHeader={!isAlert}
            fixedFooter={!isAlert}
            className={resolvedClassName}
            dataClassName={resolvedDataClassName}
        >
            {children}
        </DhlModal>
    );

    return portalToBody && typeof document !== 'undefined'
        ? createPortal(modal, document.body)
        : modal;
};

export default DhlModalWrapperLogin;
